<script setup lang="ts">
import { RouterLink } from 'vue-router'
import type { Component } from 'vue'
import AuctionCard from '@/components/auction/AuctionCard.vue'
import { useI18n } from '@/composables/useI18n'
import type { Auction } from '@/types'

interface Props {
  title: string
  subtitle?: string
  to: string
  items: Auction[]
  icon?: Component
  accent?: 'live' | 'gold' | 'ink'
  id: string
}

withDefaults(defineProps<Props>(), { accent: 'ink' })
const { t } = useI18n()

const accentClass: Record<string, string> = {
  live: 'bg-live/10 text-live',
  gold: 'bg-primary/20 text-onPrimaryContainer',
  ink: 'bg-accent text-text-primary',
}
</script>

<template>
  <section v-if="items.length" :aria-labelledby="`${id}-title`" class="space-y-4">
    <div class="flex items-start justify-between gap-3 sm:items-end">
      <div class="flex min-w-0 items-start gap-3 sm:items-center">
        <span v-if="icon" class="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:mt-0" :class="accentClass[accent]">
          <component :is="icon" class="h-4 w-4" />
        </span>
        <div class="min-w-0">
          <h2 :id="`${id}-title`" class="font-display text-lg leading-tight sm:text-2xl font-bold tracking-tight text-text-primary">{{ title }}</h2>
          <p v-if="subtitle" class="mt-0.5 hidden text-sm text-text-secondary sm:block">{{ subtitle }}</p>
        </div>
      </div>
      <RouterLink
        :to="to"
        :aria-label="`${t('common.viewAll')}: ${title}`"
        class="-my-2.5 -mr-2.5 inline-flex min-h-[40px] shrink-0 items-center gap-1 whitespace-nowrap rounded-xl px-2.5 py-2 text-sm font-semibold text-text-primary transition hover:bg-accent hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {{ t('common.viewAll') }}
        <span aria-hidden="true">→</span>
      </RouterLink>
    </div>

    <div class="rail lg:mx-0 lg:grid lg:grid-cols-4 lg:gap-4 lg:overflow-visible lg:px-0">
      <AuctionCard
        v-for="auction in items"
        :key="auction.id"
        :auction="auction"
        class="w-[236px] sm:w-[260px] lg:w-auto"
      />
    </div>
  </section>
</template>
